/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useIntl, FormattedMessage, defineMessages} from 'react-intl'
import {Helmet} from 'react-helmet'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

import loadable from '@loadable/component'
const PageNotFound = loadable(() => import('./../page-not-found'))

// Components
import {
    Box,
    Flex,
    SimpleGrid,
    Select,
    FormControl,
    useDisclosure,
    useMultiStyleConfig,
    Tabs,
    TabPanels,
    TabPanel,
    Button,
    Text,
    useBreakpointValue,
} from '@chakra-ui/react'

// Project Components
// import Pagination from '@salesforce/retail-react-app/app/components/pagination'

import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import EmptySearchResults from './partials/empty-results'
import PageHeader from './partials/page-header'
import AbovePageHeader from './partials/above-page-header'

// Hooks
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {useSortUrls} from '../../hooks/use-sort-urls'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
// Others
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Constants
import {PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '../../hooks/use-wish-list'
import {useWishlistActions} from '../../hooks/use-wishlist-actions'
import {isHydrated, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import CompareBanner from './partials/compare-banner'
import {remCalc} from '../../theme/_utils'
import useCustomGetCategory from '../../custom-hooks/use-custom-categories'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import ProductFilterDrawer from './partials/product-filter-drawer'
import {removeSiteLocaleFromPath} from '@salesforce/retail-react-app/app/utils/url'
import {
    useUsid,
    useShopperContext,
    useCustomer,
    useCustomerId,
} from '@salesforce/commerce-sdk-react'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'
import PlpGridCard from '../../components/editorial-components/plp-grid-card'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import ProductManuals from '../paperless/partials/product-manuals'
import SearchCategoryTabs from './partials/search-category-tabs'
import FaqArticles from './partials/faq-articles'
import usePHPBKApi from '../../custom-api/hooks/use-phpkb-api'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {usePlpProducts} from '../../custom-hooks/use-plp-products'
import AlternateMetaTag from '../../components/metatag/alternate'
import {usePlpGtmEvent} from '../../custom-hooks/use-plp-gtm-event'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {getDamProductUrl, getDamBaseUrl} from '../../custom-hooks/use-dam-image'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getMetaTags} from '../../utils/utils'
import {MAX_CACHE_AGE_PLP, STALE_WHILE_REVALIDATE_PLP} from '../../constants'
import {getProductUrl} from '../../hooks/use-product-data'
import {BRAND_LABEL, BRAND_KEY} from '../../brand'
import useManuals from '../../hooks/use-manuals'
//import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import CategoryCardFilters from './partials/category-card-filters'
import usePlpFilters from './hooks/use-plp-filters'
import FiltersSection from './partials/filters-section'
import {CompareProvider} from '../../context/compare'

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
    const isBrandDelonghi = BRAND_KEY === 'delonghi'
    // Using destructuring to omit properties; we must rename `isLoading` because we use a different
    // `isLoading` later in this function.
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const styles = useMultiStyleConfig('ProductList')
    const stylesGetMoreButton = useMultiStyleConfig('GetMoreButton')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const navigate = useNavigation()
    const isMobile = useBreakpointValue({base: true, sm: true, md: false})
    const location = useLocation()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const {addLayer} = useGoogleTagManager()

    /**************** Page State ****************/
    const [faqArticlesHits, setFaqArticlesHits] = useState(0)
    const {customParams} = getConfig()
    const {site, locale} = useMultiSite()
    const productGridColumns = [1, 1, 2, 3]

    const {urlSegment} = useParams()
    const urlSearchParams = new URLSearchParams(location.search)
    let searchQuery = urlSearchParams.get('q')
    const customerId = useCustomerId()
    const isSearch = !!searchQuery
    const pathnameNoLocale = removeSiteLocaleFromPath(location.pathname)
    const categoriesData = useGetCategoriesData()
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const {buildLocalizedUrl} = useMulticountryUtils()
    const {getProductManualsInLanguage} = useManuals()
    const {usePHPBKFreeTextSearch} = usePHPBKApi()

    const {usid} = useUsid()
    const {data: shopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    const urlSegmentValid = !!urlSegment && urlSegment !== 'null'

    const {data: categoryData} = useCustomGetCategory(
        {parameters: {urlSegment: pathnameNoLocale}},
        {enabled: !isSearch && urlSegmentValid},
    )
    const isCategoryDisabled = categoryData?.c_isDisabled === 'true'

    const {data: customer} = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer},
    )

    //we hide the price filter for non shopable countries
    // const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    // const exludedFilterslist = !notShoppableCountry ? ['cgid'] : ['cgid', 'price'] || []

    const {
        productSearchResult,
        productsCount,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError: isPlpFetchProductsError,
        previousPage,
        isLoading,
        prefetchNextPage,
        prefetchPreviousPage,
        isFirstPageFetched,
    } = usePlpProducts(
        {
            isSearch,
            urlSegment: pathnameNoLocale,
            searchParams,
            category: categoryData,
            expand: ['represented_products', 'page_meta_tags'],
        },
        {
            enabled: isSearch || urlSegmentValid,
        },
    )

    const {data: faqSearchData} = usePHPBKFreeTextSearch(
        {q: searchQuery},
        {enabled: Boolean(isSearch && searchQuery)},
    )

    const faqHitsCount = faqSearchData?.hits?.hits?.length ?? faqArticlesHits

    const manualsHitsCount = useMemo(() => {
        if (!isSearch || !productSearchResult?.hits?.length) return 0

        return productSearchResult.hits.reduce((acc, product) => {
            const productManuals =
                getProductManualsInLanguage(product?.representedProduct, locale.id)?.manuals || []
            return acc + productManuals.length
        }, 0)
    }, [isSearch, productSearchResult?.hits, getProductManualsInLanguage, locale.id])
    const {activeFiltersCount, categoryRefinements, category, toggleFilter, resetFilters} =
        usePlpFilters({
            searchParams,
            stringifySearchParams,
            shopperContext,
            customer,
            navigate,
            pathnameNoLocale,
            isSearch,
            addLayer,
            productSearchResult,
            isBrandDelonghi,
            categoryData,
        })

    const allHits = productSearchResult?.hits || []

    const pageIsPlpPlus = categoryData?.c_isPlpPlus === 'true'
    const plpPlusData = categoryData?.plpPlusData

    /**************** Response Handling ****************/
    if (res && allHits.length > 0) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_PLP}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_PLP}`,
        )
    }

    /**************** Render Variables ****************/
    //const basePath = `${location.pathname}${location.search}`
    const showNoResults =
        !isLoading && productSearchResult && productSearchResult?.hits?.length === 0

    const {sortingOptions} = productSearchResult || {}
    const selectedSortingOptionLabel =
        sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption,
        ) ?? productSearchResult?.selectedSortingOption

    // Get urls to be used for pagination, page size changes, and sorting.

    const sortUrls = useSortUrls({options: sortingOptions})

    /**************** Action Handlers ****************/
    const {data: wishlist} = useWishList()
    const {addItemToWishlist, removeItemFromWishlist, isProductInWishlist} =
        useWishlistActions(wishlist)

    const handleResetFilters = () => resetFilters(onClose)
    /**************** Einstein ****************/
    useEffect(() => {
        if (productSearchResult) {
            if (isSearch) {
                try {
                    // einstein.sendViewSearch(searchQuery, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewSearch error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, searchQuery},
                    })
                }
                activeData.sendViewSearch(searchParams, productSearchResult)
            } else {
                try {
                    // einstein.sendViewCategory(category, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewCategory error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, category},
                    })
                }
                activeData.sendViewCategory(searchParams, category, productSearchResult)
            }
        }
    }, [productSearchResult])

    function ProductTileLoop(productSearchItems) {
        return productSearchItems?.map((productSearchItem, i) => {
            const productId = productSearchItem?.productId
            const isInWishlist = isProductInWishlist(productId)

            const handleProductClick = () => {
                if (searchQuery) {
                    // einstein.sendClickSearch(searchQuery, productSearchItem)
                } else if (category) {
                    // einstein.sendClickCategory(category, productSearchItem)
                }
            }

            return (
                <ProductTile
                    data-testid={`sf-product-tile-${productSearchItem.productId}`}
                    key={productSearchItem.productId}
                    product={productSearchItem}
                    productIndex={i}
                    enableFavourite
                    isFavourite={isInWishlist}
                    selectableAttributeId={PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID}
                    //gridView={gridView}
                    onClick={handleProductClick}
                    onFavouriteToggle={(toBeFavourite) => {
                        const action = toBeFavourite ? addItemToWishlist : removeItemFromWishlist
                        return action(productSearchItem)
                    }}
                    dynamicImageProps={{
                        widths: ['50vw', '50vw', '20vw', '20vw', '25vw'],
                    }}
                    categoryName={categoryData?.name}
                    isFromProductList={true}
                    isFetchPriority={i === 0 && !pageIsPlpPlus}
                />
            )
        })
    }
    function ProductTileLoopEditorial(productSearchItems) {
        const hasActiveFilters = activeFiltersCount > 0

        const gridEditorialComponents = plpPlusData?.grid_editorial_component ?? []
        const editorialComponentsCount = gridEditorialComponents?.length

        const isGridComponents2 = categoryData?.c_isPlpPlus2 === 'true'
        const isGridComponents1 =
            categoryData?.c_isPlpPlus === 'true' || categoryData?.c_isPlpPlus2 === 'true'
        if (
            (!isGridComponents1 && !isGridComponents2) ||
            editorialComponentsCount === 0 ||
            !isFirstPageFetched
        )
            return ProductTileLoop(productSearchItems)

        //Here we controll the positions of the products and the editorial grid components
        const firstPart = productSearchItems.slice(0, 3)
        const secondPart = productSearchItems.slice(3, 5)
        const thirdPart = productSearchItems.slice(5)

        const gridCardProps = gridEditorialComponents?.[0]
            ? {
                  colSpan: 1,
                  badge: gridEditorialComponents?.[0]?.grid_component_badge,
                  title: gridEditorialComponents?.[0]?.grid_component_title,
                  subtitle: gridEditorialComponents?.[0]?.grid_component_subtitle,
                  images: gridEditorialComponents?.[0].images,
                  redirects: gridEditorialComponents?.[0].redirects,
                  footer: gridEditorialComponents?.[0].bottom_blocks,
                  textColor: gridEditorialComponents?.[0].text_color,
                  categoriesData,
              }
            : null

        const gridCardProps2 = gridEditorialComponents?.[1]
            ? {
                  colSpan: 1,
                  title: gridEditorialComponents?.[1].grid_component_title,
                  subtitle: gridEditorialComponents?.[1]?.grid_component_subtitle,
                  badge: gridEditorialComponents?.[1]?.grid_component_badge,
                  images: gridEditorialComponents?.[1].images,
                  redirects: gridEditorialComponents?.[1].redirects,
                  footer: gridEditorialComponents?.[1].bottom_blocks,
                  textColor: gridEditorialComponents?.[1].text_color,
                  categoriesData,
              }
            : null

        const visibleGridCards = [
            !hasActiveFilters && gridCardProps && isGridComponents1 ? (
                <PlpGridCard key="plp-grid-card-1" {...gridCardProps} />
            ) : null,
            !hasActiveFilters && gridCardProps2 && isGridComponents2 ? (
                <PlpGridCard key="plp-grid-card-2" {...gridCardProps2} />
            ) : null,
        ].filter(Boolean)

        return (
            <>
                {ProductTileLoop(firstPart)}
                {ProductTileLoop(secondPart)}
                {visibleGridCards}
                {ProductTileLoop(thirdPart)}
            </>
        )
    }

    const tabButtonLabels = defineMessages({
        productResults: {
            id: 'product_list.product_results',
            defaultMessage: 'Products ({total})',
        },
        faq: {
            id: 'product_list.frequently_asked_questions',
            defaultMessage: 'Frequently Asked Questions ({total})',
        },
        manuals: {
            id: 'product_list.instruction_manuals',
            defaultMessage: 'Instruction Manuals ({total})',
        },
    })
    const tabKeys = Object.keys(tabButtonLabels)
    const getTabIndexFromSearchType = () => {
        const searchType = urlSearchParams.get('searchType')
        const resolvedIndex = tabKeys.indexOf(searchType || 'productResults')
        return resolvedIndex >= 0 ? resolvedIndex : 0
    }

    const [tabIndex, setTabIndex] = useState(getTabIndexFromSearchType)

    const handleTabChange = (i) => {
        const selectedTabKey = tabKeys[i] || 'productResults'

        if (selectedTabKey === 'productResults') urlSearchParams.delete('searchType')
        else urlSearchParams.set('searchType', selectedTabKey)

        setTabIndex(i)
        navigate(`${location.pathname}?${urlSearchParams.toString()}`)
    }

    useEffect(() => {
        setTabIndex(getTabIndexFromSearchType())
    }, [location.search])

    //?GTM Hook for PLP
    usePlpGtmEvent(productSearchResult, isSearch, categoryData, pageIsPlpPlus, isLoading)

    const currentPathname = categoryData?.c_slugUrl
        ? buildLocalizedUrl(categoryData?.c_slugUrl)
        : location.pathname
    const currentPath = 'https://' + customParams.currentHost + currentPathname

    const categories = category?.current?.parentCategoryTree || category?.parentCategoryTree || []

    const categoryName =
        categories?.length > 0 ? categories[categories.length - 1]?.name : 'Product List Page'

    const metaDescription =
        productSearchResult?.pageMetaTags?.find((tag) => tag.id === 'description')?.value || ''

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: categoryName,
        description: metaDescription,
        url: currentPath,
    }

    const itemListElement = allHits.map((product, index) => {
        return {
            '@type': 'ListItem',
            position: index + 1,
            url: 'https://' + customParams.currentHost + getProductUrl(product),
        }
    })

    const itemListSchemaMarkup = {
        '@context': 'http://schema.org',
        '@type': 'ItemList',
        itemListElement,
    }

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: categories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }

    /**************** Error Handling ****************/
    if (!isLoading && (categoryData?.error || isPlpFetchProductsError)) {
        if (res) res.status(404)
        return <PageNotFound />
    }
    if (isCategoryDisabled) {
        return <PageNotFound />
    }

    return (
        <Box
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            data-gtm={isSearch ? 'internal-search-result-page' : 'product-list-page'}
            {...rest}
            background="background.neutral.default"
        >
            <Helmet>
                {getMetaTags(productSearchResult?.pageMetaTags)}

                <link rel="preconnect" href={getDamProductUrl()} />
                {pageIsPlpPlus && <link rel="preconnect" href={getDamBaseUrl()} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(itemListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                <link rel="canonical" href={currentPath} />
            </Helmet>
            {categoryData?.c_hreflangs && <AlternateMetaTag links={categoryData.c_hreflangs} />}
            <Box layerStyle="page">
                <AbovePageHeader />
                <PageHeader
                    searchQuery={searchQuery}
                    category={category.current}
                    isLoading={isLoading}
                    editorialComponents={
                        pageIsPlpPlus || plpPlusData?.top_editorial_components
                            ? plpPlusData?.top_editorial_components
                            : undefined
                    }
                />
            </Box>
            {!showNoResults ? (
                <Tabs isLazy onChange={handleTabChange} index={tabIndex}>
                    <Box layerStyle="page">
                        <SearchCategoryTabs
                            productSearchResult={productSearchResult}
                            tabButtonLabels={tabButtonLabels}
                            faqArticlesHits={faqHitsCount}
                            manualsHits={manualsHitsCount}
                            isSearch={isSearch}
                            selectedIndex={tabIndex}
                        />
                    </Box>
                    <TabPanels>
                        <TabPanel px={remCalc(0)}>
                            <Box layerStyle="page">
                                <CategoryCardFilters
                                    categoryRefinements={categoryRefinements}
                                    toggleFilter={toggleFilter}
                                    selectedFilters={searchParams.refine}
                                />
                                <FiltersSection
                                    productSearchResult={productSearchResult}
                                    toggleFilter={toggleFilter}
                                    handleResetFilters={handleResetFilters}
                                    onOpen={onOpen}
                                    sortUrls={sortUrls}
                                    activeFiltersCount={activeFiltersCount}
                                    selectedSortingOptionLabel={selectedSortingOptionLabel}
                                />
                                <Flex
                                    width="100%"
                                    mt={{
                                        base: remCalc(12),
                                        sm: remCalc(12),
                                        md: remCalc(14),
                                        lg: remCalc(14),
                                    }}
                                    marginBottom={{
                                        base: remCalc(12),
                                        sm: remCalc(12),
                                        md: remCalc(18),
                                        lg: remCalc(18),
                                    }}
                                    justifyContent="flex-start"
                                >
                                    {!isMobile && (
                                        <Text
                                            textStyle="body-xxsmall"
                                            fontWeight="semibold"
                                            color="foreground.neutral.subtle"
                                            textAlign="left"
                                        >
                                            {productSearchResult?.total}{' '}
                                            <FormattedMessage
                                                id="plp.results.total_title"
                                                defaultMessage="PRODUCTS"
                                            />
                                        </Text>
                                    )}
                                </Flex>
                                {(hasPreviousPage || isFetchingPreviousPage) && !isLoading && (
                                    <Flex justify="center">
                                        <ViewPreviousButton
                                            buildLocalizedUrl={buildLocalizedUrl}
                                            previousPage={previousPage}
                                            fetchPreviousPage={fetchPreviousPage}
                                            productSearchResult={productSearchResult}
                                            isFetchingPreviousPage={isFetchingPreviousPage}
                                            prefetchPreviousPage={prefetchPreviousPage}
                                            customParams={customParams}
                                            location={location}
                                        />
                                    </Flex>
                                )}
                                <SimpleGrid
                                    className="sf-product-grid"
                                    data-testid="sf-product-grid"
                                    columns={productGridColumns}
                                    {...styles.productsGrid}
                                >
                                    {isHydrated() && isLoading
                                        ? new Array(searchParams.limit)
                                              .fill(0)
                                              .map((value, index) => (
                                                  <ProductTileSkeleton key={index} />
                                              ))
                                        : ProductTileLoopEditorial(allHits)}
                                </SimpleGrid>
                                <Flex {...stylesGetMoreButton.container}>
                                    <Text {...stylesGetMoreButton.label}>
                                        <FormattedMessage
                                            defaultMessage={
                                                'You are viewing {alreadyLoadedItems} of {totalItems} products'
                                            }
                                            values={{
                                                alreadyLoadedItems:
                                                    BRAND_LABEL === 'Kenwood' ||
                                                    BRAND_LABEL === 'Braun' ? (
                                                        <b>{productsCount}</b>
                                                    ) : (
                                                        productsCount
                                                    ),
                                                totalItems:
                                                    BRAND_LABEL === 'Kenwood' ||
                                                    BRAND_LABEL === 'Braun' ? (
                                                        <b>{productSearchResult?.total ?? 0}</b>
                                                    ) : (
                                                        productSearchResult?.total ?? 0
                                                    ),
                                            }}
                                            id="product_list.getmore.itemcount"
                                        />
                                    </Text>

                                    {(hasNextPage || isFetchingNextPage) && !isLoading && (
                                        <ViewMoreButton
                                            fetchNextPage={fetchNextPage}
                                            productSearchResult={productSearchResult}
                                            isFetchingNextPage={isFetchingNextPage}
                                            stylesGetMoreButton={stylesGetMoreButton}
                                            prefetchNextPage={prefetchNextPage}
                                            buildLocalizedUrl={buildLocalizedUrl}
                                            customParams={customParams}
                                            location={location}
                                        />
                                    )}
                                </Flex>
                            </Box>
                            {(pageIsPlpPlus || plpPlusData?.bottom_editorial_components) &&
                                plpPlusData?.bottom_editorial_components.map((component) => (
                                    <Box
                                        key={component.uid}
                                        {...styles.editorialComponentsContainer}
                                    >
                                        <EditorialComponentsFactory
                                            componentType={component?._content_type_uid}
                                            {...component}
                                            titleAs={
                                                component?._content_type_uid === 'text_group' ||
                                                component?._content_type_uid === 'sidecard'
                                                    ? 'h2'
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                ))}
                        </TabPanel>
                        <TabPanel px={remCalc(0)}>
                            <Box layerStyle="page" pt={{base: remCalc(8), lg: remCalc(24)}}>
                                {isSearch && (
                                    <FaqArticles
                                        searchQuery={searchQuery}
                                        setFaqArticlesHits={setFaqArticlesHits}
                                    />
                                )}
                            </Box>
                        </TabPanel>
                        <TabPanel {...styles.manualsTabPanel}>
                            <Box layerStyle="page">
                                {isSearch &&
                                    productSearchResult?.hits?.map((product) => {
                                        const productManuals = getProductManualsInLanguage(
                                            product?.representedProduct,
                                            locale.id,
                                        )

                                        return (
                                            <ProductManuals
                                                key={product.productId}
                                                product={product?.representedProduct}
                                                productManuals={productManuals?.manuals || []}
                                                variant="plpManuals"
                                            />
                                        )
                                    })}
                            </Box>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            ) : (
                <EmptySearchResults searchQuery={searchQuery} category={category.current} />
            )}
            <ProductFilterDrawer
                searchParams={searchParams}
                productSearchResult={productSearchResult}
                selectedSortingOptionLabel={selectedSortingOptionLabel}
                sortUrls={sortUrls}
                category={category.current}
                isOpen={isOpen}
                onClose={onClose}
                filtersLoading={isLoading}
                toggleFilter={toggleFilter}
                resetFilters={handleResetFilters}
            />
            <CompareBanner categoryId={categoryData?.id} />
            <Box id="plp_product_recommentations" />
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object,
}

const ProductListContainer = (props) => {
    return (
        <CompareProvider>
            <ProductList {...props} />
        </CompareProvider>
    )
}

export default ProductListContainer

const Sort = ({sortUrls, productSearchResult, basePath, ...otherProps}) => {
    const intl = useIntl()
    const history = useHistory()

    return (
        <FormControl
            aria-label={intl.formatMessage({
                id: 'product_list.drawer.title.sort_by',
                defaultMessage: 'Sort By',
            })}
            data-testid="sf-product-list-sort"
            id="page_sort"
            width="auto"
            {...otherProps}
        >
            <Select
                value={basePath.replace(/(offset)=(\d+)/i, '$1=0')}
                onChange={({target}) => {
                    history.push(target.value)
                }}
                height={11}
                width="240px"
            >
                {sortUrls.map((href, index) => (
                    <option key={href} value={href}>
                        {intl.formatMessage(
                            {
                                id: 'product_list.select.sort_by',
                                defaultMessage: 'Sort By: {sortOption}',
                            },
                            {
                                sortOption: productSearchResult?.sortingOptions[index]?.label,
                            },
                        )}
                    </option>
                ))}
            </Select>
        </FormControl>
    )
}

Sort.propTypes = {
    sortUrls: PropTypes.array,
    productSearchResult: PropTypes.object,
    basePath: PropTypes.string,
}

const ViewMoreButton = ({
    fetchNextPage,
    productSearchResult,
    isFetchingNextPage,
    stylesGetMoreButton,
    prefetchNextPage,
    buildLocalizedUrl,
    customParams,
    location,
}) => {
    useEffect(() => {
        prefetchNextPage()
    }, [productSearchResult.page])

    return (
        <Button
            as="a"
            variant="outline-not-rounded"
            size="large"
            data-cs-label="plp-showMore"
            onClick={(e) => {
                e.preventDefault()
                fetchNextPage()
            }}
            href={`https://${customParams.currentHost}${buildLocalizedUrl(
                location.pathname,
            )}?page=${productSearchResult.page + 1}`}
            isLoading={isFetchingNextPage}
            {...stylesGetMoreButton?.buttonAdditional}
            type="button"
        >
            <FormattedMessage defaultMessage="view more" id="product_list.getmore.loadmorebutton" />
        </Button>
    )
}

ViewMoreButton.propTypes = {
    fetchNextPage: PropTypes.func.isRequired,
    productSearchResult: PropTypes.object.isRequired,
    isFetchingNextPage: PropTypes.bool.isRequired,
    stylesGetMoreButton: PropTypes.object,
    prefetchNextPage: PropTypes.func.isRequired,
    buildLocalizedUrl: PropTypes.func.isRequired,
    customParams: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
}

const ViewPreviousButton = ({
    previousPage,
    isFetchingPreviousPage,
    stylesGetMoreButton,
    fetchPreviousPage,
    buildLocalizedUrl,
    prefetchPreviousPage,
    customParams,
    location,
}) => {
    useEffect(() => {
        prefetchPreviousPage()
    }, [previousPage])

    return (
        <Button
            as="a"
            variant="outline-not-rounded"
            size="large"
            onClick={(e) => {
                e.preventDefault()
                fetchPreviousPage()
            }}
            href={`https://${customParams.currentHost}${buildLocalizedUrl(location.pathname)}${
                previousPage > 1 ? `?page=${previousPage}` : ''
            }`}
            isLoading={isFetchingPreviousPage}
            {...stylesGetMoreButton?.buttonAdditional}
            margin="auto"
            marginBottom={remCalc(16)}
            type="button"
        >
            <FormattedMessage
                defaultMessage={'view previous'}
                // values={{addMore: addMoreItems}}
                id="product_list.getprevious.loadmorebutton"
            />
        </Button>
    )
}

ViewPreviousButton.propTypes = {
    previousPage: PropTypes.number,
    fetchPreviousPage: PropTypes.func.isRequired,
    productSearchResult: PropTypes.object.isRequired,
    isFetchingPreviousPage: PropTypes.bool.isRequired,
    stylesGetMoreButton: PropTypes.object,
    buildLocalizedUrl: PropTypes.func.isRequired,
    prefetchPreviousPage: PropTypes.func.isRequired,
    customParams: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
}
